import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { estimationsApi } from '../api/estimations';
import { usersApi } from '../api/users';
import useAuthStore from '../store/authStore';

export default function EstimationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdminOrManager } = useAuthStore();
  const [estimation, setEstimation] = useState(null);
  const [shares, setShares] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [showShare, setShowShare] = useState(false);
  const [shareUserId, setShareUserId] = useState('');
  const [shareLevel, setShareLevel] = useState('VIEW');
  const [allUsers, setAllUsers] = useState([]);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [estRes, commentsRes, sharesRes] = await Promise.all([
        estimationsApi.get(id),
        estimationsApi.getComments(id),
        estimationsApi.getShares(id),
      ]);
      setEstimation(estRes.data);
      setComments(commentsRes.data);
      setShares(sharesRes.data);
    } catch (err) {
      console.error(err);
      navigate('/estimations');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const res = await usersApi.list({ page_size: 100 });
      setAllUsers(res.data.users.filter((u) => u.id !== estimation?.owner_user_id));
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      await estimationsApi.addComment(id, newComment);
      setNewComment('');
      const commentsRes = await estimationsApi.getComments(id);
      setComments(commentsRes.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error');
    }
  };

  const handleShare = async () => {
    if (!shareUserId) return;
    try {
      await estimationsApi.share(id, parseInt(shareUserId), shareLevel);
      setShowShare(false);
      setShareUserId('');
      const sharesRes = await estimationsApi.getShares(id);
      setShares(sharesRes.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error');
    }
  };

  const handleRevokeShare = async (shareId) => {
    if (!window.confirm('¿Revocar acceso?')) return;
    try {
      await estimationsApi.revokeShare(id, shareId);
      setShares(shares.filter((s) => s.id !== shareId));
    } catch (err) {
      alert(err.response?.data?.detail || 'Error');
    }
  };

  const canEdit = estimation && (estimation.owner_user_id === user?.id || estimation.shared_with_me === 'EDIT' || isAdminOrManager());
  const canComment = estimation && (estimation.owner_user_id === user?.id || ['EDIT', 'COMMENT'].includes(estimation.shared_with_me) || isAdminOrManager());
  const canShare = estimation && (estimation.owner_user_id === user?.id || isAdminOrManager());

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
  if (!estimation) return null;

  const phases = estimation.phases_json ? JSON.parse(estimation.phases_json) : {};
  const phaseEntries = Object.entries(phases);

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button onClick={() => navigate('/estimations')} className="text-gray-400 hover:text-gray-600 text-sm">
            ← Estimaciones
          </button>
          <h1 className="text-2xl font-bold text-gray-800 mt-1">{estimation.project_name}</h1>
          <p className="text-sm text-gray-500">
            por {estimation.owner_username} · {new Date(estimation.created_at).toLocaleDateString('es-MX')}
            {estimation.shared_with_me && (
              <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                Compartido: {estimation.shared_with_me}
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          {canShare && (
            <button
              onClick={() => { setShowShare(!showShare); loadUsers(); }}
              className="px-3 py-2 bg-purple-100 text-purple-700 rounded-lg text-sm hover:bg-purple-200"
            >
              🔗 Compartir
            </button>
          )}
          {canEdit && (
            <button className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
              ✏️ Editar
            </button>
          )}
        </div>
      </div>

      {/* Share dialog */}
      {showShare && (
        <div className="bg-white rounded-xl border border-purple-200 p-4">
          <h3 className="font-semibold text-gray-800 mb-3">Compartir estimación</h3>
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <label className="text-xs text-gray-500">Usuario</label>
              <select
                value={shareUserId}
                onChange={(e) => setShareUserId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">Seleccionar...</option>
                {allUsers.map((u) => (
                  <option key={u.id} value={u.id}>{u.full_name || u.username} ({u.email})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500">Permiso</label>
              <select
                value={shareLevel}
                onChange={(e) => setShareLevel(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="VIEW">VIEW — Solo ver</option>
                <option value="COMMENT">COMMENT — Comentar</option>
                <option value="EDIT">EDIT — Editar</option>
              </select>
            </div>
            <button onClick={handleShare} className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm">
              Compartir
            </button>
          </div>

          {/* Current shares */}
          {shares.length > 0 && (
            <div className="mt-4 space-y-2">
              <p className="text-xs text-gray-500 font-medium">Compartido con:</p>
              {shares.map((share) => (
                <div key={share.id} className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg">
                  <span className="text-sm">{share.shared_with_username}</span>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">{share.permission_level}</span>
                    <button onClick={() => handleRevokeShare(share.id)} className="text-xs text-red-500 hover:text-red-700">
                      Revocar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500">Complejidad</p>
          <p className="text-lg font-bold">{estimation.complexity}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500">Horas totales</p>
          <p className="text-lg font-bold text-blue-600">{estimation.total_hours}h</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500">Confianza</p>
          <p className="text-lg font-bold">{estimation.confidence}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500">Estado</p>
          <p className="text-lg font-bold">{estimation.status}</p>
        </div>
      </div>

      {/* Description */}
      {estimation.description && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-800 mb-2">Descripción</h3>
          <p className="text-sm text-gray-600 whitespace-pre-wrap">{estimation.description}</p>
        </div>
      )}

      {/* Phases */}
      {phaseEntries.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-800 mb-4">Fases SDLC</h3>
          <div className="space-y-2">
            {phaseEntries.map(([name, data]) => (
              <div key={name} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                <span className="text-sm text-gray-700">{name}</span>
                <span className="text-sm font-medium">{data.hours}h</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Comments */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-800 mb-4">💬 Comentarios ({comments.length})</h3>

        <div className="space-y-3 mb-4">
          {comments.length === 0 && (
            <p className="text-sm text-gray-400">Sin comentarios aún.</p>
          )}
          {comments.map((comment) => (
            <div key={comment.id} className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-medium text-gray-800">
                  {comment.full_name || comment.username}
                </span>
                <span className="text-xs text-gray-400">
                  {new Date(comment.created_at).toLocaleString('es-MX')}
                </span>
              </div>
              <p className="text-sm text-gray-600">{comment.content}</p>
            </div>
          ))}
        </div>

        {canComment && (
          <div className="flex gap-2">
            <input
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Escribe un comentario..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
              onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
            />
            <button
              onClick={handleAddComment}
              disabled={!newComment.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
            >
              Enviar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
